import { uniqBy } from 'lodash'
import { useState } from 'react'

import {
  FilterOptions,
  FilterQueryOptionValue,
  SearchableEntityProps,
} from '@ui/v2/TableFilter/TableFilter'

import { FilterQueryProps } from '@model/FilterFields'
import { NameDto } from '@model/NameDto'

interface ActiveSearchableFilterItemsParams {
  groupSearchProps?: SearchableEntityProps
  tradingAccountSearchProps?: SearchableEntityProps
  clientsSearchProps?: SearchableEntityProps
  campaignSearchProps?: SearchableEntityProps
  categoriesSearchProps?: SearchableEntityProps
  languagesSearchProps?: SearchableEntityProps
  sizesSearchProps?: SearchableEntityProps
  searchFilters?: FilterQueryProps
}

interface ActiveSearchableFilterItemsValue {
  activeSearchableFilterItems?: Partial<Record<FilterOptions, FilterQueryOptionValue>>
  setActiveSearchableFilterItems: (
    value?: Partial<Record<FilterOptions, FilterQueryOptionValue>>
  ) => void
  onActiveSearchableFilterItemsChange: (value?: FilterQueryProps) => void
}

export const useActiveSearchableFilterItems = (
  props: ActiveSearchableFilterItemsParams
): ActiveSearchableFilterItemsValue => {
  const {
    groupSearchProps,
    tradingAccountSearchProps,
    clientsSearchProps,
    campaignSearchProps,
    categoriesSearchProps,
    languagesSearchProps,
    sizesSearchProps,
  } = props

  const [activeSearchableFilterItems, setActiveSearchableFilterItems] =
    useState<Partial<Record<FilterOptions, FilterQueryOptionValue>>>()

  const onActiveSearchableFilterItemsChange = (value?: FilterQueryProps) => {
    const update = (
      filterOption: FilterOptions,
      ids: string[] | undefined,
      options: NameDto<string | number>[] | undefined
    ) => {
      setActiveSearchableFilterItems((prev) => {
        if (!ids?.length) return { ...prev, [filterOption]: undefined }
        const prevOptions = prev?.[filterOption] || []
        const nextOptions = uniqBy([...(prevOptions || []), ...(options || [])], 'id').filter((o) =>
          ids.includes(o.id.toString())
        )
        const isSame =
          prevOptions.length === nextOptions.length &&
          prevOptions.every((o, i) => o.id === nextOptions[i].id)
        if (isSame) return prev
        return { ...prev, [filterOption]: nextOptions }
      })
    }

    update(
      FilterOptions.IntroducingBrokerGroupId,
      (value?.[FilterOptions.IntroducingBrokerGroupId] as string)?.split(' '),
      groupSearchProps?.options
    )
    update(
      FilterOptions.RebateGroupId,
      (value?.[FilterOptions.RebateGroupId] as string)?.split(' '),
      groupSearchProps?.options
    )
    update(
      FilterOptions.IBRebateGroupId,
      (value?.[FilterOptions.IBRebateGroupId] as string)?.split(' '),
      groupSearchProps?.options
    )
    update(
      FilterOptions.TradingAccountId,
      (value?.[FilterOptions.TradingAccountId] as string)?.split(' '),
      tradingAccountSearchProps?.options
    )
    update(
      FilterOptions.TradingAccount,
      (value?.[FilterOptions.TradingAccount] as string)?.split(' '),
      tradingAccountSearchProps?.options
    )
    update(
      FilterOptions.SizeId,
      (value?.[FilterOptions.SizeId] as string)?.split(' '),
      sizesSearchProps?.options
    )
    update(
      FilterOptions.CampaignId,
      (value?.[FilterOptions.CampaignId] as string)?.split(' '),
      campaignSearchProps?.options
    )
    update(
      FilterOptions.CategoryId,
      (value?.[FilterOptions.CategoryId] as string)?.split(' '),
      categoriesSearchProps?.options
    )
    update(
      FilterOptions.LanguageId,
      (value?.[FilterOptions.LanguageId] as string)?.split(' '),
      languagesSearchProps?.options
    )
    update(
      FilterOptions.ClientId,
      (value?.[FilterOptions.ClientId] as string)?.split(' '),
      clientsSearchProps?.options
    )
  }

  return {
    activeSearchableFilterItems,
    setActiveSearchableFilterItems,
    onActiveSearchableFilterItemsChange,
  }
}
